import type { ReactNode } from 'react';

import Link from 'next/link';

import { cn } from '../lib/utils';
import {
  TelefonBearbeitenKnopf,
  TelefonBearbeitungRahmen,
} from './feld-bearbeitung-telefon';

/**
 * Karte — weisse Fläche, 12 px, Schatten statt Rahmen (subcontracts `Section`, #1329).
 *
 * Eine Fläche trägt EINE Kartenebene: was darin steht, gliedern Trennlinien
 * und Abstand (`ListenZeile`, `Feldraster`, `Leer`), nie eine zweite Karte.
 *
 * 🔴 ALLE KARTEN SIND GLEICH (Auftraggeber 05.10.2026, T20): weiss, Schatten,
 * KEIN Rahmen und kein Ring — auch nicht für „die Karte, auf der entschieden
 * wird". Hervorhebung geht nur über `ton` (Randbalken) oder über den Inhalt.
 * Deshalb nimmt die Karte keine `className`: der Aufrufer setzt Raster und
 * Klebeposition an einem umgebenden Element, nicht an der Fläche.
 *
 * `ton` — ein Hinweis, der auf dem Grund steht (Phase pausiert, Frist läuft
 * ab), ist eine Karte mit 4-px-Randbalken in der Zustandsfarbe, wie
 * subcontracts `card px-5 py-4 border-l-4 border-warn`
 * (`kundenvertraege/[id]/page.tsx:105`). Keine getönte Fläche auf dem Grund
 * (R-15, #1329 T19b): die verschwindet auf #eff4fc.
 *
 * `randlos` — der Inhalt reicht bis an die Kante (Tabelle, Liste mit eigenen
 * Zeilen-Polstern, Bild). Der Kopf behält sein Polster.
 *
 * `href` — die ganze Karte ist ein Verweis. Gebaut als GESTRECKTER
 * Titel-Link (`after:absolute after:inset-0`), nicht als `<a>` um die Karte:
 * darin stehen oft weitere Verweise oder Knöpfe, und `<a>` in `<a>` ist
 * ungültiges HTML (Hydration-Fehler, R-14). Kopf-Aktionen liegen per
 * `relative z-10` über der gestreckten Fläche und bleiben bedienbar; ein
 * weiterer Verweis im Inhalt braucht dasselbe `relative z-10`.
 *
 * Bearbeiten am Telefon (07.10.2026): trägt die Karte ein `Feldraster` mit
 * bearbeitbaren Feldern, steht unter 768 px EIN „Bearbeiten" im Kopf statt
 * eines Stifts je Feld (`feld-bearbeitung-telefon.tsx`). Ohne solches Raster
 * rendert der Knopf nichts.
 */
const TON = {
  warn: 'border-l-warning-text border-l-4',
  err: 'border-l-danger border-l-4',
  /*
    `eigen` — kein Zustand, sondern die RICHTUNG „von uns": die gesendete
    Nachricht im Verlauf (Auftraggeber-Befund 07.10.2026: „setzt sich farblich
    nicht ab"). Indigo, weil Indigo die Struktur trägt, nicht die Handlung
    (Vermillion); derselbe Balken für Locari und jedes Mitglied — er markiert
    die Seite, nicht eine Person. Balken gegen Weiss 7,70:1.
  */
  eigen: 'border-l-primary border-l-4',
} as const;

export function Karte({
  titel,
  meta,
  beschreibung,
  aktionen,
  dicht = false,
  fuellt = false,
  randlos = false,
  href,
  ton,
  id,
  children,
  'data-test': dataTest,
}: {
  titel?: ReactNode;
  meta?: ReactNode;
  /** Ein Satz unter dem Titel, gedämpft — was die Karte trägt. */
  beschreibung?: ReactNode;
  aktionen?: ReactNode;
  dicht?: boolean;
  /** Füllt die Höhe der Rasterzelle — Karten in einer Reihe sind gleich hoch (subcontracts Übersicht). */
  fuellt?: boolean;
  /** Inhalt bis an die Kante (Tabelle, Bild); nur der Kopf hat Polster. */
  randlos?: boolean;
  /** Die ganze Karte führt hierhin — über den gestreckten Titel-Link. Verlangt `titel`. */
  href?: string;
  ton?: keyof typeof TON;
  /** Sprungziel (`#abschnitt`). */
  id?: string;
  children?: ReactNode;
  'data-test'?: string;
}) {
  const hatKopf = Boolean(titel || meta || aktionen || beschreibung);
  const polster = dicht ? 'px-5 py-4' : 'px-6 py-5';
  const kopfPolster = dicht ? 'px-5 pt-4' : 'px-6 pt-5';

  return (
    <TelefonBearbeitungRahmen>
      <section
        id={id}
        data-test={dataTest}
        data-klickbar={href ? 'true' : undefined}
        className={cn(
          'bg-card rounded-card shadow-karte',
          randlos ? 'overflow-hidden' : polster,
          fuellt && 'h-full',
          // Randlos UND füllend: der Inhalt kann die Resthöhe nehmen
          // (`min-h-0 flex-1 overflow-y-auto`) — Listen mit eigenem Rollbereich.
          randlos && fuellt && 'flex flex-col',
          href && 'relative',
          ton && TON[ton],
        )}
      >
        {hatKopf ? (
          <div
            className={cn(randlos ? cn(kopfPolster, 'shrink-0 pb-3') : 'mb-3')}
          >
            {/*
            `flex-wrap`: reicht die Breite nicht für Titel UND Aktionen, rutschen
            die Aktionen unter den Titel — statt den Titel am Telefon auf drei
            Zeilen neben einem Knopf zu quetschen (Feinschliff 06.10.2026,
            Kalender-Karte bei 390 px).
          */}
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
              <div className="flex min-w-0 items-baseline gap-3">
                {titel ? (
                  <h2 className="text-lg font-semibold">
                    {href ? (
                      <Link
                        href={href}
                        className="hover:text-primary after:absolute after:inset-0 after:content-['']"
                      >
                        {titel}
                      </Link>
                    ) : (
                      titel
                    )}
                  </h2>
                ) : null}
                {meta ? (
                  <span className="text-muted-foreground text-xs">{meta}</span>
                ) : null}
              </div>
              {aktionen ? (
                <div
                  className={cn(
                    'flex shrink-0 items-center gap-2',
                    href && 'relative z-10',
                  )}
                >
                  {aktionen}
                  <TelefonBearbeitenKnopf />
                </div>
              ) : (
                <TelefonBearbeitenKnopf />
              )}
            </div>
            {beschreibung ? (
              <p className="text-muted-foreground mt-1 text-sm">
                {beschreibung}
              </p>
            ) : null}
          </div>
        ) : (
          <TelefonBearbeitenKnopf alleinstehend />
        )}
        {children}
      </section>
    </TelefonBearbeitungRahmen>
  );
}
